import { combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';

import { TitleCasePipe } from '@angular/common';
import {
  AfterViewChecked,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';

import { untilDestroyed } from '@s2a-core/ng-core';
import { EquipmentQueryService, EquipmentService } from '@s2a-equipment/core';
import { EmbedAppMountedPath } from '@s2a/core';

import { environment } from '../../../environments/environment';
import { ComponentLifecycleManager } from '../../services/component.service';
import { PageLoaderService } from '../../services/pageLoader.service';
import { RouterService } from '../../services/router.service';

/**
 * RAG chat destination (S2ACN-5740).
 * Prod portal must open prod LibreChat; test/dev keep the trial instance so the
 * two environments never share users or conversations.
 */
const RAG_CHAT_URL = environment.production ? 'https://ai-buddy.share2act.cn' : 'https://chat-trial.share2act.cn';

@Component({
  selector: 's2a-shell',
  templateUrl: './shell.component.html',
  styleUrls: ['./shell.component.scss'],
  encapsulation: ViewEncapsulation.ShadowDom,
  providers: [TitleCasePipe],
})
export class ShellComponent implements OnInit, OnDestroy, AfterViewChecked {
  @ViewChild('contentPlace') contentPlace: ElementRef;
  @ViewChild('ragFab') ragFab: ElementRef<HTMLElement>;

  activatedAppUrl: string;
  isInitialized = false;
  private untilDestroyed = untilDestroyed();
  private fabPositionLoaded = false;

  constructor(
    private componentLifecycleManager: ComponentLifecycleManager,
    private activeRoute: ActivatedRoute,
    private router: Router,
    private routerService: RouterService,
    public pageLoaderService: PageLoaderService,
    private equipmentService: EquipmentService,
    private equipmentQueryService: EquipmentQueryService,
    private titleCasePipe: TitleCasePipe,
    private title: Title,
  ) {}

  /** RAG Chat FAB click: JWT token + account flow for seamless SSO (S2ACN-5740) */
  openRagChat(): void {
    const token = localStorage.getItem('LOCALSTORAGE_AUTH_TOKEN');
    let url = RAG_CHAT_URL;
    if (token) {
      let account = '';
      try {
        const session = JSON.parse(sessionStorage.getItem('LOCALSTORAGE_USER_SESSION') || '{}');
        account = session.account || '';
      } catch (e) {
        account = '';
      }
      url += `#token=${encodeURIComponent(token)}`;
      if (account) {
        url += `&account=${encodeURIComponent(account)}`;
      }
    }
    window.open(url, '_blank', 'noopener');
  }

  /** FAB drag support - simple sync update, no rAF lag */
  onFabMouseDown(event: MouseEvent): void {
    event.preventDefault();
    const fab = event.currentTarget as HTMLElement;
    const startX = event.clientX;
    const startY = event.clientY;
    const rect = fab.getBoundingClientRect();
    const offsetX = startX - rect.left;
    const offsetY = startY - rect.top;
    let moved = false;

    // Use pointer capture to ensure we get all mouse events
    const pointerId = (event as PointerEvent).pointerId as number | undefined;
    if (fab.setPointerCapture && pointerId !== undefined) {
      fab.setPointerCapture(pointerId);
    }

    const onMouseMove = (e: MouseEvent) => {
      const dx = e.clientX - startX;
      const dy = e.clientY - startY;
      if (!moved && (Math.abs(dx) > 3 || Math.abs(dy) > 3)) {
        moved = true;
        fab.style.transition = 'none';
        fab.style.cursor = 'grabbing';
      }
      if (moved) {
        const x = e.clientX - offsetX;
        const y = e.clientY - offsetY;
        fab.style.transform = `translate(${x}px, ${y}px)`;
        fab.style.left = '0';
        fab.style.top = '0';
        fab.style.right = 'auto';
        fab.style.bottom = 'auto';
      }
    };

    const onMouseUp = (e: MouseEvent) => {
      if (fab.hasPointerCapture?.(pointerId)) {
        fab.releasePointerCapture(pointerId);
      }
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      fab.style.transition = '';
      fab.style.cursor = 'grab';
      if (moved) {
        const x = e.clientX - offsetX;
        const y = e.clientY - offsetY;
        localStorage.setItem('rag-fab-position', JSON.stringify({ left: x, top: y }));
      }
      fab.dataset.dragged = moved ? 'true' : 'false';
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }

  /** FAB click - only if not dragged */
  onFabClick(event: MouseEvent): void {
    const fab = event.currentTarget as HTMLElement;
    if (fab.dataset.dragged === 'true') {
      fab.dataset.dragged = 'false';
      event.preventDefault();
      event.stopPropagation();

      return;
    }
    this.openRagChat();
  }

  ngOnInit(): void {
    this.equipmentService.ensureCache().pipe(this.untilDestroyed()).subscribe();
    combineLatest([
      this.activeRoute.data.pipe(
        map((routeData) => {
          this.activatedAppUrl = routeData.appUrl || '';
          if (this.activatedAppUrl.includes('errors')) {
            return 'errors';
          }

          return this.activatedAppUrl;
        }),
      ),
      this.equipmentQueryService
        .selectFilter('description', 'enterprise')
        .pipe(map((equipments) => (equipments.length ? equipments[0] : null))),
    ])
      .pipe(this.untilDestroyed())
      .subscribe(([service, equipment]) => {
        this.setTitle(service, equipment ? equipment.description : '');
      });

    this.routerService
      .getActiveRoute$()
      .pipe(this.untilDestroyed())
      .subscribe((event: MessageEvent) => {
        this.navigateShell(event);
      });
  }

  navigateShell(event: MessageEvent): void {
    if (event.data && 'value' in event.data) {
      const value = event.data.value;
      const currentPath = location.href.replace(`${location.origin}/`, '');
      const embedAppPath = value.startsWith(`${EmbedAppMountedPath}/`)
        ? value.replace(`${EmbedAppMountedPath}/`, '')
        : value;
      const urlSegment = this.getUrlSegment(embedAppPath as string);

      if (embedAppPath !== currentPath) {
        void this.router.navigate([`/${urlSegment.path}`], {
          queryParams: urlSegment.queryParams,
          replaceUrl: true,
        });
      }
    }
  }

  getUrlSegment(url: string): { path: string; queryParams?: any } {
    const queryStringStartIndex = url.indexOf('?');
    if (queryStringStartIndex > -1) {
      const queryParamsIterator = new URLSearchParams(url.slice(queryStringStartIndex + 1));
      const queryParams = {};
      queryParamsIterator.forEach((value, key) => {
        queryParams[key] = value;
      });

      return {
        path: url.slice(0, queryStringStartIndex),
        queryParams,
      };
    }

    return {
      path: url,
    };
  }

  setTitle(service: string, equipmentTitle: string): void {
    this.title.setTitle(
      `${equipmentTitle ? `${this.titleCasePipe.transform(equipmentTitle)} - ` : ''}${
        service ? `${this.titleCasePipe.transform(service)} - ` : ''
      } Share2Act`,
    );
  }

  ngAfterViewChecked(): void {
    if (!this.componentLifecycleManager.contentShell) {
      this.componentLifecycleManager.createComponent(this.contentPlace);
      this.renderApp();
    } else {
      if (!this.isInitialized) {
        this.renderApp();
      }
    }
    // Restore FAB position.
    // ShadowDom: document.querySelector() cannot see this template, so use the
    // template reference. (Without this the saved position was never applied.)
    if (!this.fabPositionLoaded) {
      const fab = this.ragFab?.nativeElement;
      if (fab) {
        const saved = localStorage.getItem('rag-fab-position');
        if (saved) {
          try {
            const pos = JSON.parse(saved);
            if (pos.left !== undefined && pos.top !== undefined) {
              fab.style.transform = `translate(${pos.left}px, ${pos.top}px)`;
              fab.style.left = '0';
              fab.style.top = '0';
              fab.style.right = 'auto';
              fab.style.bottom = 'auto';
            }
          } catch {
            // ignore parse error
          }
        }
        this.fabPositionLoaded = true;
      }
    }
  }

  renderApp(): void {
    this.pageLoaderService.render(environment.domainUrl.concat(`${EmbedAppMountedPath}${this.getUrl()}`));
    this.isInitialized = true;
  }

  ngOnDestroy(): void {
    this.componentLifecycleManager.detachComponent();
    this.componentLifecycleManager.clear();
  }

  private getUrl(): string {
    const url = location.href;
    const urlWithDomain = url.replace(location.origin, '');
    if (!urlWithDomain.startsWith(`/${this.activatedAppUrl}`)) {
      return `/${this.activatedAppUrl}`;
    }

    return urlWithDomain;
  }
}
